// components/SelfPaced/StudentCertificatesPage.js
// "My Certificates" — themed, responsive, light/dark mode
// Full certification dashboard with detail view, in-app PDF viewer, download
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import {
  Award, Download, Calendar, Hash, BookOpen,
  Eye, Trophy, CheckCircle2, X,
  BarChart3, GraduationCap,
} from 'lucide-react';
import { AppButton, AppBadge, EmptyState, PageHeader, SearchInput, FilterBar, FilterSelect } from '../ui';
import CommonToaster from '../Common/CommonToaster';
import { getMyCertificates, getCertificate } from '../../utils/selfPacedApi';
import './_self-paced-pages.scss';

/* ── Certificate PDF Viewer Modal ───────────────────────────── */
const CertificateViewerModal = ({ cert, pdfUrl, onClose, onDownload, downloading, loadingPdf }) => {
  const course = cert?.course || cert?.courseId || {};
  if (!cert) return null;

  return (
    <div className="spce-modal-overlay" onClick={onClose}>
      <div className="spce-viewer-modal" onClick={e => e.stopPropagation()}>
        <div className="spce-viewer-modal__header">
          <div className="spce-viewer-modal__title">
            <Award size={18} />
            <span>{course.title || course.name || 'Certificate'}</span>
          </div>
          <div className="spce-viewer-modal__actions">
            <AppButton
              size="sm"
              onClick={() => onDownload(cert)}
              disabled={downloading === cert._id}
            >
              {downloading === cert._id ? <Spinner size="sm" animation="border" /> : <><Download size={14} /> Download</>}
            </AppButton>
            <button className="spce-viewer-modal__close" onClick={onClose}><X size={18} /></button>
          </div>
        </div>
        <div className="spce-viewer-modal__body">
          {loadingPdf ? (
            <div className="spce-viewer-modal__loading">
              <Spinner animation="border" variant="secondary" />
              <p>Loading certificate...</p>
            </div>
          ) : pdfUrl ? (
            <iframe
              src={pdfUrl}
              title="Certificate PDF"
              className="spce-viewer-modal__pdf"
            />
          ) : (
            <div className="spce-viewer-modal__loading">
              <p>Certificate PDF not available yet. Try downloading.</p>
            </div>
          )}
        </div>

        {/* Details strip */}
        <div className="spce-viewer-modal__details">
          {cert.certificateNo && (
            <span className="spce-viewer-modal__detail"><Hash size={13} /> {cert.certificateNo}</span>
          )}
          {cert.issueDate && (
            <span className="spce-viewer-modal__detail"><Calendar size={13} /> {new Date(cert.issueDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
          )}
          {cert.studentName && (
            <span className="spce-viewer-modal__detail"><GraduationCap size={13} /> {cert.studentName}</span>
          )}
          {cert.quizScore != null && (
            <span className="spce-viewer-modal__detail"><BarChart3 size={13} /> Score: {cert.quizScore}%</span>
          )}
          {cert.completionPct != null && (
            <span className="spce-viewer-modal__detail"><CheckCircle2 size={13} /> {cert.completionPct}% Complete</span>
          )}
        </div>
      </div>
    </div>
  );
};

/* ── Certificate Card ──────────────────────────────────────── */
const CertificateCard = ({ cert, onView, onDownload, downloading }) => {
  const course = cert.course || cert.courseId || {};

  return (
    <div className="spce-card" onClick={() => onView(cert)} role="button" tabIndex={0}>
      <div className="spce-card__badge">
        <div className="spce-card__badge-ring">
          <Award size={28} />
        </div>
        {cert.quizScore != null && (
          <span className="spce-card__score">
            <Trophy size={11} /> {cert.quizScore}%
          </span>
        )}
      </div>
      <div className="spce-card__body">
        <span className="spce-card__label">Certificate of Completion</span>
        <h4 className="spce-card__course">{course.title || course.name || 'Course'}</h4>

        <div className="spce-card__details">
          {cert.certificateNo && (
            <span><Hash size={13} /> {cert.certificateNo}</span>
          )}
          {cert.issueDate && (
            <span><Calendar size={13} /> {new Date(cert.issueDate).toLocaleDateString()}</span>
          )}
          {course.category && (
            <span><BookOpen size={13} /> {course.category}</span>
          )}
        </div>
      </div>
      <div className="spce-card__footer">
        <AppButton
          size="sm"
          variant="outline"
          onClick={(e) => { e.stopPropagation(); onDownload(cert); }}
          disabled={downloading === cert._id}
        >
          {downloading === cert._id ? <Spinner size="sm" animation="border" /> : <><Download size={14} /> Download</>}
        </AppButton>
        <AppButton
          size="sm"
          variant="ghost"
          onClick={(e) => { e.stopPropagation(); onView(cert); }}
        >
          <Eye size={14} /> View
        </AppButton>
      </div>
    </div>
  );
};

/* ── Sort Options ──────────────────────────────────────────── */
const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'name', label: 'Course Name' },
];

/* ── Main Page ──────────────────────────────────────────────── */
export default function StudentCertificatesPage() {
  const navigate = useNavigate();
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [selectedCert, setSelectedCert] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('newest');
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const res = await getMyCertificates();
      setCerts(res.data?.data || res.data || []);
    } catch {
      setToast({ show: true, message: 'Failed to load certificates', type: 'error' });
    } finally { setLoading(false); }
  };

  // Fetch presigned URL for a cert
  const fetchPdfUrl = async (cert) => {
    const courseId = cert.course?._id || cert.courseId?._id || cert.courseId || cert.course;
    const res = await getCertificate(courseId);
    const data = res.data?.data || res.data;
    return data?.downloadUrl || data?.url || null;
  };

  // View: open in-app PDF viewer modal
  const handleView = async (cert) => {
    setSelectedCert(cert);
    setPdfUrl(null);
    setLoadingPdf(true);
    try {
      const url = await fetchPdfUrl(cert);
      setPdfUrl(url);
    } catch {
      setToast({ show: true, message: 'Failed to load certificate preview', type: 'error' });
    } finally { setLoadingPdf(false); }
  };

  // Download: fetch blob and trigger save-as
  const handleDownload = async (cert) => {
    try {
      setDownloading(cert._id);
      const url = await fetchPdfUrl(cert);
      if (!url) {
        setToast({ show: true, message: 'Certificate download not available yet. Please try again later.', type: 'error' });
        return;
      }
      const resp = await fetch(url);
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${cert.certificateNo || 'certificate'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      setToast({ show: true, message: 'Download failed', type: 'error' });
    } finally { setDownloading(null); }
  };

  const filtered = useMemo(() => {
    let list = [...certs];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(c => {
        const course = c.course || c.courseId || {};
        return (course.title || course.name || '').toLowerCase().includes(q)
          || (c.certificateNo || '').toLowerCase().includes(q)
          || (c.studentName || '').toLowerCase().includes(q);
      });
    }
    if (sort === 'newest') list.sort((a, b) => new Date(b.issueDate) - new Date(a.issueDate));
    if (sort === 'oldest') list.sort((a, b) => new Date(a.issueDate) - new Date(b.issueDate));
    if (sort === 'name') list.sort((a, b) => {
      const na = (a.course || a.courseId || {}).title || (a.course || a.courseId || {}).name || '';
      const nb = (b.course || b.courseId || {}).title || (b.course || b.courseId || {}).name || '';
      return na.localeCompare(nb);
    });
    return list;
  }, [certs, search, sort]);

  return (
    <div className="sp-page">
      <PageHeader
        title="My Certificates"
        subtitle={`${certs.length} certificate${certs.length !== 1 ? 's' : ''} earned`}
        actions={
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <AppBadge color="amber"><Award size={12} /> {certs.length} Earned</AppBadge>
          </div>
        }
      />

      {certs.length > 0 && (
        <FilterBar
          actions={<SearchInput placeholder="Search certificates..." value={search} onChange={setSearch} />}
        >
          <FilterSelect value={sort} onChange={setSort} options={SORT_OPTIONS} />
          <AppBadge variant="secondary">{filtered.length} certificate{filtered.length !== 1 ? 's' : ''}</AppBadge>
        </FilterBar>
      )}

      {loading ? (
        <div className="sp-loading"><Spinner animation="border" variant="secondary" /></div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<Award size={28} />}
          title={certs.length === 0 ? 'No certificates yet' : 'No matching certificates'}
          description={certs.length === 0 ? 'Complete a course to earn your certificate of completion.' : 'Try adjusting your search.'}
          action={certs.length === 0 ? <AppButton onClick={() => navigate('/s/self-paced/enrollments')}>My Learning</AppButton> : undefined}
        />
      ) : (
        <div className="spce-grid">
          {filtered.map(cert => (
            <CertificateCard
              key={cert._id}
              cert={cert}
              onView={handleView}
              onDownload={handleDownload}
              downloading={downloading}
            />
          ))}
        </div>
      )}

      {/* Certificate PDF Viewer */}
      {selectedCert && (
        <CertificateViewerModal
          cert={selectedCert}
          pdfUrl={pdfUrl}
          loadingPdf={loadingPdf}
          onClose={() => { setSelectedCert(null); setPdfUrl(null); }}
          onDownload={handleDownload}
          downloading={downloading}
        />
      )}

      <CommonToaster show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast(t => ({ ...t, show: false }))} />
    </div>
  );
}
